import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Check, ArrowLeft, ArrowRight } from "lucide-react";
import PageHero from "../components/PageHero";
import CtaBand from "../components/CtaBand";
import NotFound from "./NotFound";
import { SERVICES } from "../data/content";
import { Reveal, usePageMeta } from "../lib/motion";

const ServiceDetail = () => {
  const params = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const slug = params.slug || location.pathname.replace(/^\//, "");
  const service = SERVICES.find((s) => s.slug === slug);
  usePageMeta(service ? service.title : "Service", service?.short);

  if (!service) return <NotFound />;

  return (
    <main data-testid={`service-page-${service.slug}`}>
      <PageHero eyebrow="OUR SERVICES" title={service.title.toUpperCase()} description={service.short} />
      <section className="py-20 lg:py-28 bg-navy-deep">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-3 gap-14">
          <div className="lg:col-span-2">
            <Reveal>
              <h2 className="font-display text-2xl sm:text-3xl font-bold text-white mb-6 tracking-tight">
                How we assist you
              </h2>
              <p className="text-slate-400 leading-relaxed mb-10">{service.description}</p>
            </Reveal>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {service.points.map((p, i) => (
                <Reveal key={p} delay={i * 0.08}>
                  <div className="glass-card rounded-xl p-5 flex items-start gap-3.5 h-full">
                    <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-gold/15 border border-gold/40">
                      <Check className="h-3.5 w-3.5 text-gold" />
                    </span>
                    <p className="text-sm text-slate-300 leading-relaxed">{p}</p>
                  </div>
                </Reveal>
              ))}
            </div>
            <Reveal delay={0.2}>
              <div className="mt-12 flex flex-wrap gap-4">
                <button
                  onClick={() => navigate("/eligibility")}
                  data-testid="service-eligibility-btn"
                  className="btn-gold rounded-full px-8 py-4 text-sm font-bold tracking-wider font-display inline-flex items-center gap-2"
                >
                  CHECK ELIGIBILITY <ArrowRight className="h-4 w-4" />
                </button>
                <button
                  onClick={() => navigate("/services")}
                  data-testid="service-back-btn"
                  className="btn-outline-light rounded-full px-8 py-4 text-sm font-bold tracking-wider font-display inline-flex items-center gap-2"
                >
                  <ArrowLeft className="h-4 w-4" /> ALL SERVICES
                </button>
              </div>
            </Reveal>
          </div>
          <Reveal delay={0.15}>
            <aside className="glass-card rounded-2xl p-7 h-fit lg:sticky lg:top-28">
              <h3 className="font-display text-xs font-bold tracking-[0.25em] text-gold mb-5">OTHER SERVICES</h3>
              <ul className="space-y-1">
                {SERVICES.filter((s) => s.slug !== service.slug).map((s) => (
                  <li key={s.slug}>
                    <button
                      onClick={() => navigate(`/services/${s.slug}`)}
                      data-testid={`service-sidebar-${s.slug}`}
                      className="w-full text-left px-4 py-3 rounded-lg text-sm text-slate-300 hover:text-gold hover:bg-gold/5 transition-colors"
                    >
                      {s.title}
                    </button>
                  </li>
                ))}
              </ul>
            </aside>
          </Reveal>
        </div>
      </section>
      <CtaBand />
    </main>
  );
};

export default ServiceDetail;
